import { usePageTitle } from '../../hooks/usePageTitle';
import GuideSidebar from '../../components/guides/GuideSidebar';
import GuideAccordion from '../../components/guides/GuideAccordion';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';

export default function MaintenanceSafety() {
  usePageTitle('Yacht Maintenance & Safety | Southeast Asia');

  return (
    <div className="min-h-screen pt-32 px-6 md:px-12 lg:px-24 max-w-[1600px] mx-auto pb-24">
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-12">
        <div className="lg:col-span-1"><GuideSidebar /></div>
        <div className="lg:col-span-3">
          <h1 className="text-4xl md:text-5xl font-medium mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>Maintenance & Safety</h1>
          <p className="text-lg text-white/70 mb-8">Keeping your yacht in top condition and ensuring safe cruising in Southeast Asian waters.</p>

          <div className="space-y-2">
            <GuideAccordion title="Regular Maintenance Schedule" defaultOpen={true}>
              <p><strong>Daily/Weekly (During Use):</strong></p>
              <ul className="space-y-2 mt-2">
                <li className="flex gap-2"><span className="text-gold">•</span><span>Engine checks: oil, coolant, belts</span></li>
                <li className="flex gap-2"><span className="text-gold">•</span><span>Freshwater rinse after saltwater use</span></li>
                <li className="flex gap-2"><span className="text-gold">•</span><span>Battery levels and charging</span></li>
                <li className="flex gap-2"><span className="text-gold">•</span><span>Bilge pump function</span></li>
              </ul>
              <p className="mt-4"><strong>Monthly:</strong></p>
              <ul className="space-y-2 mt-2">
                <li className="flex gap-2"><span className="text-gold">•</span><span>Hull inspection for growth (barnacles, algae)</span></li>
                <li className="flex gap-2"><span className="text-gold">•</span><span>Sacrificial anodes check (corrosion protection)</span></li>
                <li className="flex gap-2"><span className="text-gold">•</span><span>Safety equipment inspection (life jackets, flares, fire extinguishers)</span></li>
              </ul>
              <p className="mt-4"><strong>Annually:</strong></p>
              <ul className="space-y-2 mt-2">
                <li className="flex gap-2"><span className="text-gold">•</span><span>Engine service (oil change, filters, impellers)</span></li>
                <li className="flex gap-2"><span className="text-gold">•</span><span>Haul-out for bottom paint and underwater inspection</span></li>
                <li className="flex gap-2"><span className="text-gold">•</span><span>Electronics calibration (GPS, radar, depth sounder)</span></li>
                <li className="flex gap-2"><span className="text-gold">•</span><span>Safety gear replacement (expired flares, dated life rafts)</span></li>
              </ul>
              <div className="bg-white/5 p-4 mt-4 border-l-2 border-gold">
                <p className="text-white/90"><strong>Tropical Considerations:</strong> Hull growth faster in warm SE Asian waters—haul-out every 6-9 months vs. 12+ in temperate climates.</p>
              </div>
            </GuideAccordion>

            <GuideAccordion title="Essential Safety Equipment">
              <p><strong>Mandatory (Most Jurisdictions):</strong></p>
              <ul className="space-y-2 mt-2">
                <li className="flex gap-2"><span className="text-gold">•</span><span>Life jackets (one per person + extras)</span></li>
                <li className="flex gap-2"><span className="text-gold">•</span><span>Life raft (offshore cruising, sized for all crew/guests)</span></li>
                <li className="flex gap-2"><span className="text-gold">•</span><span>Flares (red hand flares, parachute rockets, orange smoke)</span></li>
                <li className="flex gap-2"><span className="text-gold">•</span><span>Fire extinguishers (multiple, engine room + galley)</span></li>
                <li className="flex gap-2"><span className="text-gold">•</span><span>First aid kit (comprehensive for remote cruising)</span></li>
                <li className="flex gap-2"><span className="text-gold">•</span><span>VHF radio with DSC (distress call capability)</span></li>
                <li className="flex gap-2"><span className="text-gold">•</span><span>EPIRB (Emergency Position Indicating Radio Beacon)</span></li>
              </ul>
              <p className="mt-4"><strong>Highly Recommended:</strong></p>
              <ul className="space-y-2 mt-2">
                <li className="flex gap-2"><span className="text-gold">•</span><span>Satellite phone (remote areas like Mergui, Raja Ampat)</span></li>
                <li className="flex gap-2"><span className="text-gold">•</span><span>AIS (Automatic Identification System) for traffic awareness</span></li>
                <li className="flex gap-2"><span className="text-gold">•</span><span>Radar (for low visibility, night navigation)</span></li>
                <li className="flex gap-2"><span className="text-gold">•</span><span>Anchor windlass and oversized anchor for coral areas</span></li>
              </ul>
            </GuideAccordion>

            <GuideAccordion title="Southeast Asia Regulations">
              <p><strong>Thailand:</strong></p>
              <ul className="space-y-2 mt-2">
                <li className="flex gap-2"><span className="text-gold">•</span><span>Cruising permit required for foreign yachts</span></li>
                <li className="flex gap-2"><span className="text-gold">•</span><span>Marine parks entry fees (Similan, Surin islands)</span></li>
                <li className="flex gap-2"><span className="text-gold">•</span><span>Seasonal closures (Similan May-October)</span></li>
              </ul>
              <p className="mt-4"><strong>Indonesia:</strong></p>
              <ul className="space-y-2 mt-2">
                <li className="flex gap-2"><span className="text-gold">•</span><span>CAIT (Cruising Permit) required, expensive but covers archipelago</span></li>
                <li className="flex gap-2"><span className="text-gold">•</span><span>Raja Ampat permit separate, dive fees</span></li>
                <li className="flex gap-2"><span className="text-gold">•</span><span>Agent often required for clearance</span></li>
              </ul>
              <p className="mt-4"><strong>Malaysia:</strong></p>
              <ul className="space-y-2 mt-2">
                <li className="flex gap-2"><span className="text-gold">•</span><span>Social visit pass for crew/guests (90 days)</span></li>
                <li className="flex gap-2"><span className="text-gold">•</span><span>Langkawi duty-free port advantages</span></li>
                <li className="flex gap-2"><span className="text-gold">•</span><span>Marine park permits (Payar, Tioman)</span></li>
              </ul>
              <div className="bg-white/5 p-4 mt-4 border-l-2 border-gold">
                <p className="text-white/90"><strong>Pro Tip:</strong> Hire local agents in Indonesia/Myanmar—they navigate bureaucracy and language barriers, worth the cost.</p>
              </div>
            </GuideAccordion>

            <GuideAccordion title="Weather & Monsoon Awareness">
              <p><strong>Northeast Monsoon (November-March):</strong></p>
              <ul className="space-y-2 mt-2">
                <li className="flex gap-2"><span className="text-gold">•</span><span>Affects: East coast Thailand, Malaysia, South China Sea</span></li>
                <li className="flex gap-2"><span className="text-gold">•</span><span>Conditions: Rough seas, heavy rain, strong winds</span></li>
                <li className="flex gap-2"><span className="text-gold">•</span><span>Action: Move to west coast (Phuket, Langkawi) or shelter</span></li>
              </ul>
              <p className="mt-4"><strong>Southwest Monsoon (May-October):</strong></p>
              <ul className="space-y-2 mt-2">
                <li className="flex gap-2"><span className="text-gold">•</span><span>Affects: Andaman Sea (west coast Thailand, Myanmar)</span></li>
                <li className="flex gap-2"><span className="text-gold">•</span><span>Conditions: Squalls, large swells from Indian Ocean</span></li>
                <li className="flex gap-2"><span className="text-gold">•</span><span>Action: Move to east coast or Malaysian waters</span></li>
              </ul>
              <p className="mt-4"><strong>Typhoon Season (July-November):</strong></p>
              <ul className="space-y-2 mt-2">
                <li className="flex gap-2"><span className="text-gold">•</span><span>Affects: Philippines, northern South China Sea, Vietnam</span></li>
                <li className="flex gap-2"><span className="text-gold">•</span><span>Action: Monitor weather closely, seek typhoon holes, or move south</span></li>
              </ul>
            </GuideAccordion>

            <GuideAccordion title="Emergency Procedures">
              <p><strong>Man Overboard:</strong></p>
              <ol className="space-y-2 mt-2">
                <li>1. Shout "Man Overboard!" + throw flotation device</li>
                <li>2. Assign spotter to keep eyes on person</li>
                <li>3. Press MOB button on GPS/chartplotter</li>
                <li>4. Motor back upwind, approach from downwind</li>
                <li>5. Use ladder/boarding platform for retrieval</li>
              </ol>
              <p className="mt-4"><strong>Grounding:</strong></p>
              <ul className="space-y-2 mt-2">
                <li className="flex gap-2"><span className="text-gold">•</span><span>Check for hull damage and leaks immediately</span></li>
                <li className="flex gap-2"><span className="text-gold">•</span><span>Note tide state (rising = wait, falling = act fast)</span></li>
                <li className="flex gap-2"><span className="text-gold">•</span><span>Try backing off gently with engine</span></li>
                <li className="flex gap-2"><span className="text-gold">•</span><span>If stuck, kedge anchor or call for tow (common in coral areas)</span></li>
              </ul>
              <p className="mt-4"><strong>Fire:</strong></p>
              <ul className="space-y-2 mt-2">
                <li className="flex gap-2"><span className="text-gold">•</span><span>Engine room: Shut off fuel, activate fire suppression</span></li>
                <li className="flex gap-2"><span className="text-gold">•</span><span>Galley: Use fire blanket or extinguisher, never water on oil fire</span></li>
                <li className="flex gap-2"><span className="text-gold">•</span><span>Abandon ship if out of control—life safety first</span></li>
              </ul>
            </GuideAccordion>
          </div>

          <div className="mt-12 flex gap-4">
            <Link to="/guides/buying-ownership"><Button variant="outline" className="border-gold text-gold hover:bg-gold hover:text-[#0A192F]">← Buying & Ownership</Button></Link>
            <Link to="/guides/trends-facts"><Button className="bg-gold text-[#0A192F] hover:bg-[#E5C55D]">Next: Trends & Facts →</Button></Link>
          </div>
        </div>
      </div>
    </div>
  );
}
